import type { RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';
import { scaleTextBagSizes, type TextBagSizes } from '@lite-workspace/dto-fotoclass-templates';

type RenderTextRun = NonNullable<RenderTextBox['runs']>[number];
type RunSizes = Pick<RenderTextRun, 'fontSize' | 'lineHeightPx' | 'baselineShift' | 'outlineWidth'>;

/**
 * Grab-time font metrics of a text box under a corner scale (FKS-11514): every px the gesture's live preview scales.
 * Frozen at grab — each frame scales from these, never from the mutated cache (the anti-compounding rule).
 */
export interface TextScaleSnapshot {
  readonly fontSize: number;
  readonly lineHeight: number;
  /** The autoFit ceiling: a size of the block, like `fontSize`. */
  readonly maxFontSize?: number;
  /** Per-run px by run index; a run of the same index scales from these. */
  readonly runSizes?: readonly RunSizes[];
  /**
   * Paragraph indents and gaps, on the box and in its paragraph runs. Required, so a producer that freezes only the font
   * does not type-check: every snapshot comes from `textScaleSnapshotOf`.
   */
  readonly paragraph: TextBagSizes;
}

export function textScaleSnapshotOf(text: RenderTextBox): TextScaleSnapshot {
  return {
    fontSize: text.fontSize,
    lineHeight: text.lineHeight,
    ...(text.maxFontSize != null && { maxFontSize: text.maxFontSize }),
    ...(text.runs && {
      runSizes: text.runs.map(r => ({
        fontSize: r.fontSize,
        lineHeightPx: r.lineHeightPx,
        baselineShift: r.baselineShift,
        outlineWidth: r.outlineWidth,
      })),
    }),
    paragraph: {
      firstLineIndent: text.firstLineIndent,
      startIndent: text.startIndent,
      endIndent: text.endIndent,
      spaceBefore: text.spaceBefore,
      spaceAfter: text.spaceAfter,
      ...(text.paragraphRuns && { paragraphRuns: [...text.paragraphRuns] }),
    },
  };
}

function scaledRunSizes(sizes: RunSizes, scale: number): RunSizes {
  return {
    ...(sizes.fontSize !== undefined && { fontSize: sizes.fontSize * scale }),
    ...(sizes.lineHeightPx !== undefined && { lineHeightPx: sizes.lineHeightPx * scale }),
    ...(sizes.baselineShift !== undefined && { baselineShift: sizes.baselineShift * scale }),
    ...(sizes.outlineWidth !== undefined && { outlineWidth: sizes.outlineWidth * scale }),
  };
}

/**
 * The live preview of a corner scale: the same px the model scales on release (`scaleTextBagSizes` and the block's
 * ceiling), so the frame under the pointer is the frame that lands. Mutates the render box in place, as the preview
 * does with the rest of the cached box.
 */
export function applyTextScale(text: RenderTextBox, snapshot: TextScaleSnapshot, scale: number): void {
  text.fontSize = snapshot.fontSize * scale;
  text.lineHeight = snapshot.lineHeight * scale;
  if (snapshot.maxFontSize !== undefined) text.maxFontSize = snapshot.maxFontSize * scale;
  const paragraph = scaleTextBagSizes(snapshot.paragraph, scale);
  if (paragraph.firstLineIndent !== undefined) text.firstLineIndent = paragraph.firstLineIndent;
  if (paragraph.startIndent !== undefined) text.startIndent = paragraph.startIndent;
  if (paragraph.endIndent !== undefined) text.endIndent = paragraph.endIndent;
  if (paragraph.spaceBefore !== undefined) text.spaceBefore = paragraph.spaceBefore;
  if (paragraph.spaceAfter !== undefined) text.spaceAfter = paragraph.spaceAfter;
  if (paragraph.paragraphRuns) text.paragraphRuns = paragraph.paragraphRuns;

  const sizes = snapshot.runSizes;
  if (text.runs && sizes) {
    text.runs = text.runs.map((run, i) => {
      const own = sizes[i];
      return own === undefined ? run : { ...run, ...scaledRunSizes(own, scale) };
    });
  }
}
